'use client'

/**
 * React Bits' `CurvedInput`, vendored — a field whose frame is a bent bar
 * rather than a rectangle, drawn as SVG so the border, the corners and the
 * accent chip all follow the same arc.
 *
 * Taken from `DavidHDev/react-bits` (`src/ts-tailwind/Components/CurvedInput`)
 * rather than installed, for the same reason `WarpText` is vendored next door:
 * the library ships source to copy, not a package to depend on. The owner
 * picked the settings as well as the component — `bend=-1, height=59,
 * cornerRadius=27, width=430, borderWidth=2` — and those are the defaults
 * below.
 *
 * ── THE ONE DECISION THIS FILE IS ABOUT ──────────────────────────────────
 * Upstream does not use a text field. It draws the value, the placeholder and
 * a blinking caret as SVG `<textPath>` running along the arc, and parks a real
 * `<input>` underneath at `opacity: 0`, `color: transparent`,
 * `caret-color: transparent`, `pointer-events: none` — a shadow field that
 * exists only to catch keystrokes. To make the drawn caret land in the right
 * place it measures `getSubStringLength`, tracks `selectionStart`, and
 * re-derives a scroll offset along the arc on every render.
 *
 * That machinery is what costs the field everything a field is for:
 *
 *  - `selectionStart` and `setSelectionRange` throw on `type="email"`, which is
 *    why upstream silently rewrites the type to `text` (`SELECTABLE_TYPES`).
 *    No e-mail keyboard on a phone, no `:invalid`, no type-aware autofill.
 *  - it forces `autoComplete="off"`, `autoCorrect="off"`, `spellCheck={false}`.
 *  - the browser's own autofill drop-down has nothing to attach to, and a
 *    password manager filling the field would write text nobody can see if the
 *    measuring pass is a frame behind.
 *  - the root element is a `<form>`, so it cannot go inside one — and both
 *    places this is used are inside a form that already owns submission.
 *
 * **So the drawn text is gone and the real `<input>` is visible.** The SVG is
 * the field's chrome — band, border, focus glow, envelope chip — and the value
 * is native HTML text sitting on top of it. Everything the brief asked to keep
 * is therefore kept by not being touched: `<label for>`, `type="email"`,
 * `autoComplete`, focus, selection, IME, autofill, `aria-invalid`.
 *
 * The honest cost: **the text no longer follows the arc.** At the owner's
 * `bend=-1` there is nothing to follow — the sagitta is one pixel across 430,
 * i.e. the bar is flat to the eye and the curve shows up only as the pill's
 * shape. {@link MAX_BEND} clamps the prop to the range where that stays true;
 * past it the straight run of text would visibly leave the bent band, and the
 * only way to fix it is the machinery described above.
 *
 * Every other change is marked `FLOU:` in the body. In short:
 *
 *  1. Root is a `<span>`, not a `<form>`. See above.
 *  2. The submit button (`showButton`, `buttonText`, the bent button path and
 *     its `<g role="button" tabIndex={0}>`) is gone. Both call sites have a
 *     real `<button type="submit">` of their own, and a fake button made of a
 *     focusable `<g>` next to a real one is two tab stops for one action.
 *  3. `THEMES`, `hexToRgba` and `SHADOWS` are gone — two hardcoded hex
 *     palettes and a drop-shadow, in a repo whose rule is semantic tokens and
 *     whose `white` swaps to ink in light mode. Colour comes from the site's
 *     own `white` utilities at `authInputClass`'s opacities, and from the
 *     brand ramp.
 *  4. `invalid`, replacing the `borderColor` prop: one flag sets the stroke AND
 *     `aria-invalid`, so what is seen and what is announced cannot drift —
 *     the rule the contact form's `FIELD` already states.
 *  5. `useId` for the gradient id (upstream needed three ids for the text and
 *     button paths, which are gone).
 *  6. The band is drawn before the width is known — see {@link ASSUMED_WIDTH}.
 *     Upstream renders nothing at all until its ResizeObserver reports, which
 *     leaves a frameless input anywhere the box has not been laid out yet.
 *  7. Named export, `'use client'`.
 *
 * The geometry — {@link buildGeometry}, `edgeSeg`, `bentRectPath` — is upstream
 * verbatim apart from the members the text layout used, and is left alone so it
 * stays diffable.
 */

import {
  type ComponentPropsWithoutRef,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { cn } from '@/lib/utils'

const DEG = 180 / Math.PI

const round2 = (n: number): number => Math.round(n * 100) / 100

/**
 * FLOU: how far the bar may bend before the promise at the top of this file
 * stops holding. The value is straight HTML text drawn across a chord; at a
 * few pixels of sagitta the gap between the text's baseline and the band's
 * centreline is under half a pixel at the ends, and invisible. At upstream's
 * default of 28 it is a quarter of the field's height and obviously wrong.
 */
const MAX_BEND = 6

interface Geometry {
  straight: boolean
  W: number
  T: number
  svgH: number
  R?: number
  dir?: number
  point: (u: number, v: number) => [number, number]
  angleAt: (u: number) => number
}

// Maps the flat coordinate space (u: 0..W along the bar, v: offset from the
// centerline, positive down) onto a circular arc with the given sagitta
// (`bend`, in px). Positive bend arches up, negative sags down, 0 is flat.
const buildGeometry = (width: number, bend: number, thickness: number, pad: number): Geometry => {
  const W = width
  const T = thickness
  const s = Math.max(-W * 0.35, Math.min(bend, W * 0.35))
  const a = Math.abs(s)
  const dir = s >= 0 ? 1 : -1
  const svgH = T + a + pad * 2

  if (a < 0.75) {
    const midY = pad + T / 2
    return {
      straight: true,
      W,
      T,
      svgH,
      point: (u, v) => [u, midY + v],
      angleAt: () => 0,
    }
  }

  const R = (W * W * 0.25 + a * a) / (2 * a)
  const cx = W / 2
  const apexY = pad + T / 2 + (dir > 0 ? 0 : a)
  const cy = apexY + dir * R
  const phi = Math.asin(Math.min(1, W / (2 * R)))

  return {
    straight: false,
    W,
    T,
    svgH,
    R,
    dir,
    point: (u, v) => {
      const th = ((u - cx) / cx) * phi
      const rho = R - dir * v
      return [cx + rho * Math.sin(th), cy - dir * rho * Math.cos(th)]
    },
    angleAt: (u) => dir * ((u - cx) / cx) * phi * DEG,
  }
}

const fmt = (g: Geometry, u: number, v: number): string => {
  const [x, y] = g.point(u, v)
  return `${round2(x)} ${round2(y)}`
}

// Segment along a constant-v edge, as a circular arc (or a line when flat)
const edgeSeg = (g: Geometry, uTo: number, v: number, ltr: boolean): string => {
  const dir = g.dir ?? 1
  if (g.straight) return `L ${fmt(g, uTo, v)}`
  const rho = round2((g.R ?? 0) - dir * v)
  const sweep = ltr === dir > 0 ? 1 : 0
  return `A ${rho} ${rho} 0 0 ${sweep} ${fmt(g, uTo, v)}`
}

// A rectangle bent along the arc: circular top/bottom edges, radial end caps
// and quadratic rounded corners.
const bentRectPath = (
  g: Geometry,
  u0: number,
  u1: number,
  vTop: number,
  vBot: number,
  radius: number,
): string => {
  const rc = Math.max(0, Math.min(radius, (vBot - vTop) / 2, (u1 - u0) / 2))
  return [
    `M ${fmt(g, u0 + rc, vTop)}`,
    edgeSeg(g, u1 - rc, vTop, true),
    `Q ${fmt(g, u1, vTop)} ${fmt(g, u1, vTop + rc)}`,
    `L ${fmt(g, u1, vBot - rc)}`,
    `Q ${fmt(g, u1, vBot)} ${fmt(g, u1 - rc, vBot)}`,
    edgeSeg(g, u0 + rc, vBot, false),
    `Q ${fmt(g, u0, vBot)} ${fmt(g, u0, vBot - rc)}`,
    `L ${fmt(g, u0, vTop + rc)}`,
    `Q ${fmt(g, u0, vTop)} ${fmt(g, u0 + rc, vTop)}`,
    'Z',
  ].join(' ')
}

/**
 * FLOU: measure before paint on the client, after mount on the server.
 * The band cannot be drawn until the container's width is known, and with a
 * plain `useEffect` the field would paint once as bare text and then grow a
 * frame later. `useLayoutEffect` alone warns on every server render.
 */
const useMeasure = typeof window === 'undefined' ? useEffect : useLayoutEffect

/** The site's error colour — the same literal the contact form's `FIELD` uses. */
const INVALID_STROKE = 'stroke-[oklch(0.72_0.19_20)]/60'

/** Focus, matching `AUTH_INPUT_FOCUS` so every field on the site agrees. */
const FOCUS_STROKE = 'group-focus-within:stroke-[oklch(0.72_0.22_350)]/60'

/**
 * FLOU: the width the band is drawn at before anything has been measured —
 * the owner's own `width=430`, which is the one setting of his that otherwise
 * had nowhere to live once the root stopped being a sized `<form>`.
 *
 * Upstream renders NOTHING until its ResizeObserver has reported (`w > 2 ?
 * … : null`), which is a real failure mode rather than a first frame: a field
 * mounted inside a subtree the browser has not laid out yet — a step of the
 * contact stepper that is still off-stage, anything behind `display: none`, a
 * server-rendered page before hydration — keeps its state at `0` and shows a
 * naked input with no frame at all until something resizes it. The band is
 * therefore always drawn, at this width when the real one is unknown, and
 * `preserveAspectRatio="none"` stretches that guess across the actual box.
 * Once the measurement lands, `viewBox` and box agree exactly and the stretch
 * is 1:1, so nothing is distorted in the normal case.
 */
const ASSUMED_WIDTH = 430

type CurvedInputProps = Omit<ComponentPropsWithoutRef<'input'>, 'className' | 'style'> & {
  /**
   * FLOU: classes for the WRAPPER, because the wrapper is what the caller lays
   * out. The input's own classes belong to this component — it has to position
   * itself over the band.
   */
  className?: string
  /** Sets the stroke and `aria-invalid` together. See note 4. */
  invalid?: boolean
  /** Sagitta in px; negative sags, positive arches. Clamped to {@link MAX_BEND}. */
  bend?: number
  /** Band thickness in px. */
  height?: number
  cornerRadius?: number
  borderWidth?: number
  /** The envelope chip at the left end. */
  showIcon?: boolean
}

export function CurvedInput({
  className,
  invalid = false,
  bend = -1,
  height = 59,
  cornerRadius = 27,
  borderWidth = 2,
  showIcon = true,
  ...input
}: CurvedInputProps) {
  const gradientId = `curved-input-${useId().replace(/:/g, '')}`
  const rootRef = useRef<HTMLSpanElement | null>(null)
  const [w, setW] = useState(0)

  useMeasure(() => {
    const el = rootRef.current
    if (!el) return
    setW(Math.round(el.clientWidth))
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect?.width ?? el.clientWidth
      setW(Math.round(next))
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const pad = Math.ceil(borderWidth / 2) + 6
  const clampedBend = Math.max(-MAX_BEND, Math.min(bend, MAX_BEND))
  const geom = useMemo(
    () => buildGeometry(w > 2 ? w : ASSUMED_WIDTH, clampedBend, height, pad),
    [w, clampedBend, height, pad],
  )

  // Upstream's chip and text-start maths, kept so the chip sits exactly where
  // it does on the demo — the only difference is that what starts at
  // `textStartU` is now the input's padding rather than a `<textPath>`.
  const chipH = Math.min(34, Math.max(16, height * 0.34))
  const chipW = chipH * 1.25
  const iconU = 22 + chipW / 2
  const textStartU = showIcon ? 22 + chipW + 13 : 24

  // Independent of the width, so the field reserves its own height from the
  // first server-rendered frame and never reflows the form under the reader.
  const svgH = geom.svgH

  return (
    <span
      ref={rootRef}
      className={cn('group relative block w-full', className)}
      style={{ height: round2(svgH) }}
    >
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 block overflow-visible"
        width={geom.W}
        height={round2(svgH)}
        viewBox={`0 0 ${geom.W} ${round2(svgH)}`}
        // FLOU: see {@link ASSUMED_WIDTH}. A no-op once measured, and what
        // keeps the frame on screen until then.
        preserveAspectRatio="none"
      >
        <defs>
          {/* `--flou-gradient`'s own stops, re-expressed as an SVG ramp: a
                CSS gradient cannot be an SVG `fill`. */}
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="var(--flou-pink)" />
            <stop offset="45%" stopColor="var(--flou-magenta)" />
            <stop offset="100%" stopColor="var(--flou-violet)" />
          </linearGradient>
        </defs>

        {/* The focus bloom: upstream's widened accent stroke behind the band,
              driven by `:focus-within` on the wrapper rather than by React
              state — the input is a sibling, so the browser can do it. */}
        <path
          className="stroke-[var(--flou-magenta)] opacity-0 transition-opacity duration-[250ms] ease-in-out group-focus-within:opacity-[0.28] motion-reduce:transition-none"
          d={bentRectPath(geom, 0, geom.W, -height / 2, height / 2, cornerRadius)}
          fill="none"
          strokeWidth={borderWidth + 6}
          // FLOU: with `preserveAspectRatio="none"` a horizontal stretch
          // would thicken the vertical sides and thin the horizontal ones.
          vectorEffect="non-scaling-stroke"
        />

        {/* The band. `fill-white` / `stroke-white` utilities rather than a
              hex in the presentation attributes: `white` is an `@theme inline`
              token here and swaps to near-black ink in light mode, so a
              literal would have been a white field on a white page. The two
              opacities are `authInputClass`'s own — `bg-white/[0.06]`,
              `border-white/12` — so this field is cut from the same glass as
              the rectangles above it. */}
        <path
          className={cn(
            'fill-white/[0.06] transition-[stroke] duration-200 motion-reduce:transition-none',
            invalid ? INVALID_STROKE : cn('stroke-white/12', FOCUS_STROKE),
          )}
          d={bentRectPath(geom, 0, geom.W, -height / 2, height / 2, cornerRadius)}
          strokeWidth={borderWidth}
          vectorEffect="non-scaling-stroke"
        />

        {showIcon ? (
          <EnvelopeChip
            fill={`url(#${gradientId})`}
            geom={geom}
            u={iconU}
            width={chipW}
            height={chipH}
          />
        ) : null}
      </svg>

      <input
        {...input}
        aria-invalid={invalid || undefined}
        className={cn(
          'absolute inset-x-0 w-full border-0 bg-transparent text-[16px] text-white outline-none placeholder:text-white/55 sm:text-sm',
          // The caret and the selection are the browser's, which is the whole
          // point of this port.
          'selection:bg-white/20',
        )}
        style={{
          // Centred on the band, whatever the sagitta adds to the SVG box.
          top: round2((svgH - height) / 2),
          height,
          paddingLeft: round2(textStartU),
          // Upstream's `textEndU` when there is no button.
          paddingRight: 24,
          // Clips the browser's autofill wash to the pill instead of letting a
          // square yellow block sit inside a rounded band.
          borderRadius: cornerRadius,
        }}
      />
    </span>
  )
}

/**
 * Upstream's default icon: a rounded chip with an envelope on it, rotated to
 * the band's tangent at that point. Verbatim apart from the fill, which is the
 * brand ramp instead of `buttonColor`.
 */
function EnvelopeChip({
  fill,
  geom,
  u,
  width,
  height,
}: {
  fill: string
  geom: Geometry
  u: number
  width: number
  height: number
}) {
  const [ix, iy] = geom.point(u, 0)
  const ew = width * 0.5
  const eh = height * 0.5
  const sw = Math.max(1.1, height * 0.075)

  return (
    <g transform={`translate(${round2(ix)} ${round2(iy)}) rotate(${round2(geom.angleAt(u))})`}>
      <rect
        x={-width / 2}
        y={-height / 2}
        width={width}
        height={height}
        rx={height * 0.27}
        fill={fill}
      />
      <rect
        x={-ew / 2}
        y={-eh / 2}
        width={ew}
        height={eh}
        rx={1.4}
        fill="none"
        stroke="#ffffff"
        strokeWidth={sw}
        strokeLinejoin="round"
      />
      <path
        d={`M ${round2(-ew / 2)} ${round2(-eh / 2 + sw * 0.4)} L 0 ${round2(eh * 0.14)} L ${round2(ew / 2)} ${round2(-eh / 2 + sw * 0.4)}`}
        fill="none"
        stroke="#ffffff"
        strokeWidth={sw}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </g>
  )
}
